'use client';

import React, { forwardRef, useMemo, useContext, useState, FC, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useGetReviewsQuery, useAddReviewMutation, useGetReviewRatingsQuery } from '@shared/api/queries';
import { formatQuestionDate, AuthContext, ModalContext, getNounForm } from '@shared/libs';
import type { Reviews, ReviewRatings } from '@shared/types';
import { H2, Button, ButtonSize, ReviewRating } from '@shared/ui';

import { AuthRequiredModal } from './auth-required-modal';
import { ReviewsPagination } from './components';
import { useReviewsPagination } from './hooks';
import styles from './product-reviews.module.css';
import { ReviewFormModal } from './review-form-modal';

// Компоненты модальных окон вынесены наружу для избежания пересоздания
interface AuthModalContentProps {
  locale: ProductReviewsProps['locale'];
  onClose?: () => void;
  onAuthSuccess?: () => void;
}

const AuthModalContent: FC<AuthModalContentProps> = ({ locale, onClose, onAuthSuccess }) => (
  <AuthRequiredModal locale={locale} onClose={onClose} onAuthSuccess={onAuthSuccess} />
);

// Интерфейс для ответа мутации добавления отзыва
interface AddReviewResponse {
  result_code: number;
  data: {
    message: string;
    review_id: number;
  };
  lang: string;
  userID: string | null;
}

interface ReviewFormModalContentProps {
  locale: ProductReviewsProps['locale'];
  onClose?: () => void;
  noteId: string;
  addReviewMutation: ReturnType<typeof useAddReviewMutation>;
  setHasNewReview: (value: boolean) => void;
  resetToFirstPage: () => void;
  onReviewSubmitted: () => void;
}

const ReviewFormModalContent: FC<ReviewFormModalContentProps> = ({
  locale,
  onClose,
  noteId,
  addReviewMutation,
  setHasNewReview,
  resetToFirstPage,
  onReviewSubmitted,
}) => {
  // Функция для отправки данных о отзыве в webhook
  const sendReviewToWebhook = async (reviewId: number, user: string, text: string, rating: number, productNoteId: string) => {
    try {
      // Получаем данные о странице
      const productUrl = typeof window !== 'undefined' ? window.location.href : '';
      const productName = typeof document !== 'undefined' ? document.title : '';
      
      // Отправляем данные в наш API endpoint
      const response = await fetch('/api-v2/reviews/send-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reviewId,
          user,
          text,
          rating,
          productUrl,
          productName,
          noteId: productNoteId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: { success: boolean; webhookResult?: unknown } = await response.json() as { success: boolean; webhookResult?: unknown };
      
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('✅ Данные о отзыве успешно отправлены в webhook:', result);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Ошибка отправки данных о отзыве в webhook:', error);
      // Не блокируем основной функционал при ошибке webhook
    }
  };

  return (
    <ReviewFormModal 
      locale={locale} 
      onClose={onClose}
      onSubmit={(data, callbacks) => {
        addReviewMutation.mutate(
          {
            noteId,
            name: data.name,
            text: data.text,
            rating: data.rating,
          },
          {
            onSuccess: (response: AddReviewResponse) => {
              setHasNewReview(true);
              resetToFirstPage();
              onReviewSubmitted();

              // Отправляем данные о отзыве в webhook
              if (response.data?.review_id) {
                void sendReviewToWebhook(response.data.review_id, data.name, data.text, data.rating, noteId);
              }

              callbacks.onSuccess();
            },
            onError: (error) => {
              console.error('Ошибка добавления отзыва:', error);
              // eslint-disable-next-line no-alert
              alert(locale.REVIEWS_FORM_ERROR_MESSAGE || 'REVIEWS_FORM_ERROR_MESSAGE');
              callbacks.onError();
            },
          }
        );
      }}
    />
  );
};

interface ProductReviewsProps {
  initialReviews?: Reviews;
  reviewsPager?: {
    pages_all: number;
    records_all: number;
    records_per_page: number;
  };
  noteId: string;
  currency: string;
  initialRatings?: ReviewRatings;
  locale: {
    REVIEWS_TITLE?: string;
    REVIEWS_EMPTY_MESSAGE?: string;
    REVIEWS_FORM_TITLE?: string;
    REVIEWS_FORM_NAME_LABEL?: string;
    REVIEWS_FORM_NAME_PLACEHOLDER?: string;
    REVIEWS_FORM_RATING_LABEL?: string;
    REVIEWS_FORM_REVIEW_PLACEHOLDER?: string;
    REVIEWS_FORM_SUBMIT_BUTTON?: string;
    REVIEWS_FORM_SUBMIT_LOADING?: string;
    REVIEWS_FORM_SUCCESS_MESSAGE?: string;
    REVIEWS_FORM_ERROR_MESSAGE?: string;
    REVIEWS_ANONYMOUS?: string;
    REVIEWS_TOTAL_COUNT?: string;
    REVIEWS_HELPFUL?: string;
    REVIEWS_NOT_HELPFUL?: string;
    WRITE_REVIEW_BUTTON?: string;
    REVIEWS_AUTH_REQUIRED_TITLE?: string;
    REVIEWS_AUTH_REQUIRED_MESSAGE?: string;
    REVIEWS_ONE?: string;
    REVIEWS_TWO?: string;
    REVIEWS_MANY?: string;
    PAGE_PERSONAL_SIGN_IN?: string;
    PAGE_PERSONAL_SIGN_IN_LOGIN?: string;
    PAGE_PERSONAL_SIGN_IN_LOGIN_PH?: string;
    PAGE_PERSONAL_SIGN_IN_PASS?: string;
    PAGE_PERSONAL_SIGN_IN_PASS_PH?: string;
    PAGE_PERSONAL_SIGN_IN_BTN?: string;
    PAGE_PERSONAL_SIGN_IN_ERROR?: string;
    PAGE_PERSONAL_SIGN_SOCIAL_AUTH?: string;
    PAGE_PERSONAL_REGISTRATION?: string;
    PAGE_PERSONAL_REGISTRATION_TITLE?: string;
    PAGE_PERSONAL_REGISTRATION_MAIL?: string;
    PAGE_PERSONAL_REGISTRATION_MAIL_PH?: string;
    PAGE_PERSONAL_REGISTRATION_PASS?: string;
    PAGE_PERSONAL_REGISTRATION_PASS_2?: string;
    PAGE_PERSONAL_REGISTRATION_PASS_PH?: string;
    PAGE_PERSONAL_REGISTRATION_PASS_ERROR?: string;
    PAGE_PERSONAL_REGISTRATION_SUCCESS?: string;
    PAGE_PERSONAL_REGISTRATION_SUCCESS_TEXT?: string;
    PAGE_PERSONAL_REGISTRATION_SUCCESS_BTN?: string;
    PAGE_PERSONAL_REGISTRATION_ERROR?: string;
    PAGE_PERSONAL_REGISTRATION_BTN?: string;
    PAGE_PERSONAL_RECOVER_PASS?: string;
    PAGE_PERSONAL_RECOVER_PASS_TITLE?: string;
    PAGE_PERSONAL_RECOVER_PASS_MAIL?: string;
    PAGE_PERSONAL_RECOVER_PASS_MAIL_PH?: string;
    PAGE_PERSONAL_RECOVER_PASS_BTN?: string;
    PAGE_PERSONAL_RECOVER_PASS_SUCCESS?: string;
    PAGE_PERSONAL_RECOVER_PASS_SUCCESS_TEXT?: string;
    PAGE_PERSONAL_RECOVER_PASS_SUCCESS_BTN?: string;
    PAGE_PERSONAL_RECOVER_PASS_ERROR?: string;
    PAGINATION_PREV?: string;
    PAGINATION_NEXT?: string;
  };
}

const ProductReviews = forwardRef<HTMLDivElement, ProductReviewsProps>(
  ({ initialReviews = [], reviewsPager, noteId, currency, initialRatings, locale }, ref) => {
    
    const [hasNewReview, setHasNewReview] = useState(false);

    // Сначала инициализируем пагинацию чтобы получить currentPage
    const {
      currentPage,
      setCurrentPage,
      resetToFirstPage,
    } = useReviewsPagination(initialReviews, reviewsPager);

    // RTK Query для страниц кроме первой, или если был добавлен новый отзыв
    const { data: clientReviewsResponse } = useGetReviewsQuery({
      noteId,
      currency,
      page: currentPage,
      pagelen: 5,
      enabled: currentPage > 1 || hasNewReview, // Включаем если не первая страница или добавлен новый отзыв
    });

    const addReviewMutation = useAddReviewMutation();

    // Клиентский запрос актуальных рейтингов (включается после добавления отзыва)
    const { data: clientRatings } = useGetReviewRatingsQuery({
      noteId,
      enabled: hasNewReview,
    });

    // Для первой страницы используем серверные данные, но если добавлен новый отзыв - RTK Query
    const reviews = (currentPage === 1 && !hasNewReview)
      ? initialReviews 
      : (clientReviewsResponse?.data || initialReviews);
    
    // Для пагинации используем актуальные данные pager из RTK Query или серверные
    const actualPager = (currentPage === 1 && !hasNewReview)
      ? reviewsPager 
      : (clientReviewsResponse?.pager || reviewsPager);
    
    // Вычисляем параметры пагинации на основе актуальных данных
    const totalPages = actualPager?.pages_all || Math.ceil(reviews.length / 5);
    const shouldShowPagination = totalPages > 1;

    const { isAuth } = useContext(AuthContext);
    const openModal = useContext(ModalContext);
    const renderReviewFormContent = useCallback(
      (props: { onClose?: () => void }) => (
        <ReviewFormModalContent 
          {...props}
          locale={locale}
          noteId={noteId}
          addReviewMutation={addReviewMutation}
          setHasNewReview={setHasNewReview}
          resetToFirstPage={resetToFirstPage}
          onReviewSubmitted={() => {}}
        />
      ),
      [locale, noteId, addReviewMutation, setHasNewReview, resetToFirstPage]
    );

    const renderAuthRequiredContent = useCallback(
      (props: { onClose?: () => void }) => (
        <AuthModalContent 
          {...props}
          locale={locale}
          onAuthSuccess={() => {
            openModal({
              modal: {
                title: locale.REVIEWS_FORM_TITLE || 'Написать отзыв',
                Content: renderReviewFormContent,
              },
            });
          }}
        />
      ),
      [locale, openModal, renderReviewFormContent]
    );


    const stats = useMemo(() => {
      // Источник данных: серверные initialRatings, а после добавления отзыва — clientRatings
      const ratingsSource = clientRatings || initialRatings;

      if (ratingsSource) {
        const distributionAsc = [
          ratingsSource.ratings[1] || 0,
          ratingsSource.ratings[2] || 0,
          ratingsSource.ratings[3] || 0,
          ratingsSource.ratings[4] || 0,
          ratingsSource.ratings[5] || 0,
        ];
        return {
          average: ratingsSource.rating_avg || 0,
          total: ratingsSource.rating_cnt || 0,
          distribution: distributionAsc,
        };
      }

      // Фолбэк на расчёт по отзывам, если нет ratingsSource
      const totalCount = reviewsPager?.records_all || actualPager?.records_all || reviews.length;
      if (reviews.length === 0) {
        return { average: 0, total: totalCount, distribution: [0, 0, 0, 0, 0] };
      }
      const total = reviews.length;
      const sum = reviews.reduce((acc, review) => acc + (review.rating || 0), 0);
      const average = sum / total;
      const distribution = [0, 0, 0, 0, 0];
      reviews.forEach((review) => {
        if (review.rating && review.rating >= 1 && review.rating <= 5) {
          distribution[review.rating - 1] = distribution[review.rating - 1] + 1;
        }
      });
      return { average, total: totalCount, distribution };
    }, [clientRatings, initialRatings, reviews, actualPager, reviewsPager]);

    // Рендерим только список отзывов
    const renderReviewsList = () => {
      return (
        <div className={styles.reviewsList}>
          {reviews.map((item) => (
            <ErrorBoundary key={`${item.name}-${item.datetime}`} fallback={null}>
              <div className={styles.reviewItem}>
                <div className={styles.reviewMetaRow}>
                  <span className={styles.reviewAuthor}>{item.name}</span>
                  <span className={styles.reviewDate}>{formatQuestionDate(item.datetime)}</span>
                </div>
                <div className={styles.reviewRatingRow}>
                  <ReviewRating rate={item.rating || 0} size="medium" />
                  {item.text && item.text.trim() ? (
                    <span className={styles.reviewText}>{item.text}</span>
                  ) : null}
                </div>
              </div>
            </ErrorBoundary>
          ))}
        </div>
      );
    };

    // Рендерим пагинацию отдельно
    const renderPagination = () => {
      if (!shouldShowPagination) return null;
      
      return (
        <ReviewsPagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          locale={{
            PAGINATION_PREV: locale.PAGINATION_PREV,
            PAGINATION_NEXT: locale.PAGINATION_NEXT,
          }}
        />
      );
    };

    const handleWriteReviewClick = () => {
      if (!isAuth) {
        openModal({
          modal: {
            title: locale.REVIEWS_AUTH_REQUIRED_TITLE || 'Требуется авторизация',
            Content: renderAuthRequiredContent,
          },
        });
      } else {
        openModal({
          modal: {
            title: locale.REVIEWS_FORM_TITLE || 'Написать отзыв',
            Content: renderReviewFormContent,
          },
        });
      }
    };

    return (
      <div className={styles.container} ref={ref}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <H2 className={styles.title}>{locale.REVIEWS_TITLE || 'REVIEWS_TITLE'}</H2>
          </div>
          <div className={`${styles.content} ${reviews.length === 0 ? styles.contentEmpty : ''}`}>
            <div className={styles.reviewsColumn}>
              {reviews.length === 0 ? (
                <div className={styles.emptyMessage}>{locale.REVIEWS_EMPTY_MESSAGE || 'REVIEWS_EMPTY_MESSAGE'}</div>
              ) : (
                <>
                  {renderReviewsList()}
                  {renderPagination()}
                </>
              )}
            </div>
            <div className={styles.statsColumn}>
              <div className={styles.statsSection}>
                <div className={styles.overallRating}>
                  <div className={styles.ratingValue}>
                    {stats.average.toFixed(1)}
                  </div>
                  <div className={styles.ratingStars}>
                    <ReviewRating rate={stats.average} size="large" />
                  </div>
                  <div className={styles.totalReviews}>
                    {stats.total} {getNounForm(stats.total, [
                      locale.REVIEWS_ONE || 'отзыв',
                      locale.REVIEWS_TWO || 'отзыва',
                      locale.REVIEWS_MANY || 'отзывов'
                    ])}
                  </div>
                </div>
                <div className={styles.ratingDistribution}>
                  {stats.distribution.map((count, index) => (
                    <div key={`rating-${index + 1}`} className={styles.ratingRow}>
                      <div className={styles.stars}>
                        <ReviewRating rate={index + 1} size="small" />
                      </div>
                      <span className={styles.ratingCount}>
                        {count} {getNounForm(count, [
                          locale.REVIEWS_ONE || 'отзыв',
                          locale.REVIEWS_TWO || 'отзыва', 
                          locale.REVIEWS_MANY || 'отзывов'
                        ])}
                      </span>
                    </div>
                  ))}
                </div>
                <Button
                  className={styles.writeReviewButton}
                  size={ButtonSize.L}
                  onClick={handleWriteReviewClick}
                >
                  {locale.WRITE_REVIEW_BUTTON || 'WRITE_REVIEW_BUTTON'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ProductReviews.displayName = 'ProductReviews';

export default ProductReviews; 